'use client';

import { useEffect, useState, type FC } from 'react';
import {
  RankingCard,
  SponsoredCard,
  type SponsoredCardProps,
  type StatField,
} from '@highereducation/hen-ui';
import { fetchRangoData, type RangoResult } from './fetchRangoData';
import {
  buildGeneralInfo,
  buildDataPanel,
  buildTestimonialsPanel,
} from './utils';
import type { RankingSchool } from './types';
import {
  fetchSponsoredOfferings,
  transformOfferingToSponsoredCardProps,
  interleave,
  parseSponsoredPositions,
} from './sponsoredOfferings';
import {
  RankingsMethodology,
  type RankingsMethodologyVariant,
} from './RankingsMethodology';
import { RankingsDisclosure } from './RankingsDisclosure';
import './blurb-check-list.css';

const METHODOLOGY_VARIANTS = new Set<RankingsMethodologyVariant>([
  'enhanced',
  'regular',
  'alternate',
]);

function isMethodologyVariant(
  value: string | undefined,
): value is RankingsMethodologyVariant {
  return (
    typeof value === 'string' &&
    METHODOLOGY_VARIANTS.has(value as RankingsMethodologyVariant)
  );
}

const EXCLUDED_SPONSORED_SCHOOL_IDS = [
  '43a39c71-4319-59b9-bd56-01caf4d0f622', // University of Phoenix
];

// Legacy Sonic rankings reserved sponsored slots at positions 3 and 9 even when
// the shortcode omitted the attribute. Mirror that default so HEN surfaces
// sponsored cards without a per-page `sponsoredpositions` content change
// (EDUN-13215). A page can still override by setting the attribute explicitly.
const DEFAULT_SPONSORED_POSITIONS = [3, 9];

// The ranking endpoint can only be filtered by degree/category/subject — it
// can't be scoped to a specific set of schools. To enrich organic cards we
// fetch a broad slice of the DCS partner inventory and match organic schools
// to it locally by `sdbSchoolId`. The limit is deliberately generous so a
// lower-ranked organic partner still shows up in the slice.
const RANKING_INVENTORY_LIMIT = 50;

/** Maps an organic school's `sdbSchoolId` to its partner lead-form data. */
type OrganicEnrichment = Record<string, { url: string; helid: string }>;

/**
 * Legacy Sonic generated the lead-attribution `helid` client-side per render.
 * `crypto.randomUUID` is available in every secure context (prod is https,
 * dev is localhost); the fallback keeps non-secure contexts from throwing.
 */
function generateHelid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `helid-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// Sentinel used to reserve sponsored positions in the interleaved slot list
// while the sponsored fetch is in flight. The renderer detects the marker via
// `isSponsoredSkeleton` and emits a placeholder pulse instead of a card.
type SponsoredSkeleton = SponsoredCardProps & { __skeleton: true };
type SponsoredSlotData = SponsoredCardProps | SponsoredSkeleton;
const SPONSORED_SKELETON: SponsoredSkeleton = {
  __skeleton: true,
  name: '',
  logoSrc: '',
  logoAlt: '',
};
function isSponsoredSkeleton(
  data: SponsoredSlotData,
): data is SponsoredSkeleton {
  return (data as SponsoredSkeleton).__skeleton === true;
}

export interface RankingsAdapterProps {
  extid?: string;
  gid?: string;
  tag?: string;
  year?: string;
  limit?: string;
  showstudenttestimonials?: string;
  methodology?: string;
  /**
   * Brand name interpolated into the methodology copy (e.g. "Psychology.org").
   * Lower-cased to match WordPress shortcode attribute conventions; the WP
   * shortcode parser emits attribute names in lower case so the prop name
   * must match. Falls through to `RankingsMethodology`'s `"this site"`
   * default when omitted.
   */
  sitename?: string;
  degree?: string;
  category?: string;
  subject?: string;
  /** Comma-separated 1-indexed positions for sponsored cards, e.g. "1,5,10". */
  sponsoredpositions?: string;
  /**
   * Tenant domain name for the advertising-disclosure tooltip, injected by
   * `RankingsShortcode.getPropsFromMeta` from the post's site settings
   * (`domainLabelWithExtension`). Takes precedence over the `sitename`
   * attribute so the disclosure names the correct domain on every tenant
   * without a per-tenant content migration. Absent in Storybook/preview
   * contexts, where the adapter falls back to `sitename` then "This site".
   */
  disclosureSiteName?: string;
  children?: React.ReactNode;
}

interface RankingCardStateProps {
  expanded: boolean;
  modalOpen: boolean;
  compared: boolean;
}

interface SponsoredCardStateProps {
  expanded: boolean;
  modalOpen: boolean;
}

function buildStats(
  school: RankingSchool,
  degreeLevel: 'undergrad' | 'grad',
  showTestimonials: boolean,
): StatField[] {
  const { stats } = buildDataPanel(school, degreeLevel);
  const testimonials = showTestimonials ? buildTestimonialsPanel(school) : null;
  const baseStats = testimonials ? [...testimonials, ...stats] : stats;
  return baseStats.map((f) => ({
    label: f.label,
    value: String(f.value),
    tooltip: f.tooltip,
  }));
}

function buildDetails(
  school: RankingSchool,
  degreeLevel: 'undergrad' | 'grad',
): StatField[] {
  const { details } = buildDataPanel(school, degreeLevel);
  return details.map((f) => ({
    label: f.label,
    value: String(f.value),
    tooltip: f.tooltip,
  }));
}

export const RankingsAdapter: FC<RankingsAdapterProps> = ({
  extid,
  gid,
  showstudenttestimonials,
  limit,
  degree,
  category,
  subject,
  sponsoredpositions,
  methodology,
  year,
  sitename,
  disclosureSiteName,
}) => {
  const [result, setResult] = useState<RangoResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [sponsoredCards, setSponsoredCards] = useState<SponsoredCardProps[]>(
    [],
  );
  // Tracks whether the sponsored fetch has resolved at least once for the
  // current dependency set. Used to render per-slot skeletons at sponsored
  // positions while Rango/sponsored are racing — keeps organic ranks stable
  // and avoids the "pop-in" shift when sponsored arrives last.
  const [sponsoredFetchSettled, setSponsoredFetchSettled] = useState(true);
  // Organic cards monetize only when matched to partner inventory by
  // `sdbSchoolId`; the matched `url` + a generated `helid` feed the same
  // `isMonetized` gate the legacy Sonic rankings used.
  const [enrichmentBySdbId, setEnrichmentBySdbId] = useState<OrganicEnrichment>(
    {},
  );
  const [cardStates, setCardStates] = useState<
    Record<string, RankingCardStateProps>
  >({});
  const [sponsoredStates, setSponsoredStates] = useState<
    Record<string, SponsoredCardStateProps>
  >({});

  const id = extid || gid || '';
  const showTestimonials = showstudenttestimonials === 'true';
  const degreeLevel = 'undergrad';
  const parsedSponsoredPositions = parseSponsoredPositions(sponsoredpositions);
  const sponsoredPositions =
    parsedSponsoredPositions.length > 0
      ? parsedSponsoredPositions
      : DEFAULT_SPONSORED_POSITIONS;
  const sponsoredEnabled = sponsoredPositions.length > 0;

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetchRangoData(id).then((data) => {
      if (!cancelled) {
        setResult(data);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [id]);

  // Constrain to the `limit`-visible set so enrichment only considers schools
  // that will actually render. A `limit` shortcode must not trigger a
  // ranking-inventory fetch for organic schools that get sliced off.
  const parsedLimit = limit ? parseInt(limit, 10) : NaN;
  const visibleSchools =
    Number.isFinite(parsedLimit) && parsedLimit > 0
      ? (result?.schools ?? []).slice(0, parsedLimit)
      : (result?.schools ?? []);

  const organicSdbSchoolIds = visibleSchools
    .map((s) => s.sdbSchoolId)
    .filter((id): id is string => Boolean(id));
  const organicSdbSchoolIdsKey = organicSdbSchoolIds.join(',');

  const hasOrganicSchoolIds = organicSdbSchoolIds.length > 0;

  useEffect(() => {
    // Skip the fetch entirely when there's nothing to do: no sponsored slots
    // to fill and no organic schools to match against partner inventory.
    if (!sponsoredEnabled && !hasOrganicSchoolIds) {
      setSponsoredCards([]);
      setEnrichmentBySdbId({});
      setSponsoredFetchSettled(true);
      return;
    }

    let stale = false;
    const controller = new AbortController();
    setSponsoredFetchSettled(false);
    // Drop the previous match map up front so a degree/category/subject change
    // can't briefly light an Explore CTA against stale partner inventory while
    // the new fetch is in flight; it repopulates when the fetch resolves.
    setEnrichmentBySdbId({});

    const presentationUrl =
      typeof window !== 'undefined' ? window.location.href : undefined;

    const excludeSet = new Set([
      ...organicSdbSchoolIds,
      ...EXCLUDED_SPONSORED_SCHOOL_IDS,
    ]);

    // One broad fetch serves both surfaces: organic cards match it by
    // `sdbSchoolId` for the Explore CTA, and the leftover (non-organic)
    // inventory fills the sponsored slots.
    const limit = Math.max(RANKING_INVENTORY_LIMIT, sponsoredPositions.length);

    fetchSponsoredOfferings(
      {
        degree,
        category,
        subject,
        presentationUrl,
        limit,
      },
      controller.signal,
    ).then((offerings) => {
      if (stale) return;

      const enrichment: OrganicEnrichment = {};
      for (const offering of offerings) {
        const sdbId = offering.organization?.meta?.sdbSchoolId;
        const url = offering.destination?.url;
        if (sdbId && url && !enrichment[sdbId]) {
          enrichment[sdbId] = { url, helid: generateHelid() };
        }
      }
      setEnrichmentBySdbId(enrichment);

      if (sponsoredEnabled) {
        const deduped = offerings.filter((o) => {
          const id = o.organization?.meta?.sdbSchoolId;
          return !id || !excludeSet.has(id);
        });
        setSponsoredCards(
          deduped
            .slice(0, sponsoredPositions.length)
            .map(transformOfferingToSponsoredCardProps),
        );
      } else {
        setSponsoredCards([]);
      }

      setSponsoredFetchSettled(true);
    });

    return () => {
      stale = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    degree,
    category,
    subject,
    sponsoredEnabled,
    hasOrganicSchoolIds,
    sponsoredPositions.length,
    organicSdbSchoolIdsKey,
  ]);

  if (loading) {
    return (
      <div
        className='flex flex-col gap-lg my-lg'
        aria-busy='true'
        aria-label='Loading rankings'
      >
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className='h-48 rounded-lg bg-neutral-100 animate-pulse'
          />
        ))}
      </div>
    );
  }

  if (!result || result.schools.length === 0) return null;

  // Attach partner lead-form data to any organic school matched in the ranking
  // inventory so the monetization gate below can light up its Explore CTA.
  // Rango-supplied url/helid (if ever present) win over the client match.
  const schools = visibleSchools.map((school) => {
    if (school.url && school.helid) return school;
    const match = school.sdbSchoolId
      ? enrichmentBySdbId[school.sdbSchoolId]
      : undefined;
    return match ? { ...school, url: match.url, helid: match.helid } : school;
  });

  const getCardState = (key: string): RankingCardStateProps =>
    cardStates[key] ?? { expanded: false, modalOpen: false, compared: false };

  const updateCardState = (
    key: string,
    patch: Partial<RankingCardStateProps>,
  ) => {
    setCardStates((prev) => ({
      ...prev,
      [key]: { ...getCardState(key), ...patch },
    }));
  };

  const getSponsoredState = (key: string): SponsoredCardStateProps =>
    sponsoredStates[key] ?? { expanded: false, modalOpen: false };

  const updateSponsoredState = (
    key: string,
    patch: Partial<SponsoredCardStateProps>,
  ) => {
    setSponsoredStates((prev) => ({
      ...prev,
      [key]: { ...getSponsoredState(key), ...patch },
    }));
  };

  // While the sponsored fetch is in flight, reserve the configured sponsored
  // positions with skeleton sentinels so organic ranks render in their final
  // slots immediately. Without this, organic items briefly occupy positions
  // 1..N and then shift when sponsored arrives.
  const sponsoredPending = sponsoredEnabled && !sponsoredFetchSettled;
  const sponsoredSlotData: SponsoredSlotData[] = sponsoredPending
    ? sponsoredPositions.map(() => SPONSORED_SKELETON)
    : sponsoredCards;
  const slots = interleave(schools, sponsoredSlotData, sponsoredPositions);

  return (
    <div className='flex flex-col gap-md md:gap-lg'>
      <RankingsDisclosure siteName={disclosureSiteName || sitename} />
      {slots.map((slot) => {
        if (slot.kind === 'sponsored') {
          if (isSponsoredSkeleton(slot.data)) {
            return (
              <div
                key={`sponsored-skel-${slot.position}`}
                className='h-48 rounded-lg bg-neutral-100 animate-pulse'
                aria-busy='true'
                aria-label='Loading sponsored card'
              />
            );
          }
          const key = `sponsored-${slot.position}-${slot.data.name}`;
          const state = getSponsoredState(key);
          return (
            <SponsoredCard
              key={key}
              {...slot.data}
              expanded={state.expanded}
              onExpandedChange={(v) =>
                updateSponsoredState(key, { expanded: v })
              }
              modalOpen={state.modalOpen}
              onModalOpenChange={(v) =>
                updateSponsoredState(key, { modalOpen: v })
              }
            />
          );
        }

        const school = slot.data;
        const key = `${school.rank}-${school.name}`;
        const state = getCardState(key);
        const badges = buildGeneralInfo(school);
        if (school.accreditation) badges.push('Accredited');
        // Matches the legacy Sonic monetization gate: both the hosted lead
        // form URL and helid must be present for the outbound CTA to render.
        const isMonetized = !!(school.url && school.helid);

        return (
          <RankingCard
            key={key}
            variant={result.type}
            rank={school.rank}
            name={school.name}
            superlative={school.superlativeTitle}
            badges={badges}
            exploreHref={isMonetized ? school.url : undefined}
            exploreLabel='Explore Program'
            stats={buildStats(school, degreeLevel, showTestimonials)}
            details={buildDetails(school, degreeLevel)}
            expanded={state.expanded}
            onExpandedChange={(v) => updateCardState(key, { expanded: v })}
            modalOpen={state.modalOpen}
            onModalOpenChange={(v) => updateCardState(key, { modalOpen: v })}
            compared={state.compared}
            onComparedChange={(v) => updateCardState(key, { compared: v })}
          >
            {school.blurb && (
              <div
                className='blurb-check-list [&>p]:mb-xs [&>p]:text-b5 [&>p]:leading-b5 [&>p]:text-subtext [&>p:first-child]:mt-0 [&>p>a]:text-primary-700 [&>p>a]:underline [&>strong]:font-bold [&>ul]:mt-xxs [&>ul]:mb-sm [&>ol]:ml-sm [&>ol]:mt-xxs [&>ol]:list-decimal [&>li]:mb-1 [&>li]:text-b5 [&>li]:text-subtext [&>h2]:font-bold [&>h2]:text-h5 [&>h2]:leading-h5 [&>h2]:mb-xxs [&>h2]:mt-xs [&>h3]:font-bold [&>h3]:text-h5 [&>h3]:leading-h5 [&>h3]:mb-xxs [&>h3]:mt-xs [&>h4]:font-bold [&>h4]:text-h5 [&>h4]:leading-h5 [&>h4]:mb-xxs'
                dangerouslySetInnerHTML={{ __html: school.blurb }}
              />
            )}
          </RankingCard>
        );
      })}
      {isMethodologyVariant(methodology) && (
        <RankingsMethodology
          variant={methodology}
          year={year}
          siteName={sitename}
        />
      )}
    </div>
  );
};
